import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { PromocodesIndexData } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { MAX_PER_PAGE, toPaginationValue, useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { usePromocodesStore } from '../model/store'

interface PromocodesApiOptions {
  route?: RouteLocationNormalizedGeneric
}

/**
 * `GET /admin-api/promocodes` — the index. Returns `{ promocodes, discounts, meta }`; `discounts`
 * is the allowed percentage set (the `product_discounts` setting) used by the create + filter selects.
 * The `from` / `to` created-at range comes straight from the date-range filter as datetime strings.
 */
export const usePromocodesApi = async (options?: PromocodesApiOptions): Promise<PromocodesIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = options?.route || useRoute()
  const store = usePromocodesStore()

  const url = useAdminRoutes('admin.promocodes.data', pick({
    q: store.getFilterSearch,
    status: store.getFilterStatus,
    discount: store.getFilterDiscount !== null ? String(store.getFilterDiscount) : undefined,
    from: store.getFilterFrom || undefined,
    to: store.getFilterTo || undefined,
    per_page: toPaginationValue(route.query?.per_page, store.getPerPage, MAX_PER_PAGE),
    page: toPaginationValue(route.query?.page, store.getCurrentPage),
  }))

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      return null
    }

    const indexData: PromocodesIndexData | null = data.data || null

    if (indexData) {
      store.setPromocodes(indexData.promocodes)
      store.setMeta(indexData.meta)
      store.setDiscounts(indexData.discounts)
    }

    return indexData
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    store.setLoading(false)
  }
}
